import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QrOrdersProvider } from './context/QrOrdersContext';
import QrOrdersModal from './components/QrOrdersModal';
import ConfirmDialogHost from './components/ConfirmDialog';
import ShiftClosedPrompt from './components/ShiftClosedPrompt';
import ImpersonationReturn from './components/ImpersonationReturn';
import OfflineBanner from './components/OfflineBanner';
import useAuthStore from './store/authStore';
import api from './api/axios';

import Login from './pages/Login';
import ProtectedRoute from './components/ProtectedRoute';

import AdminDashboard from './pages/admin/Dashboard';
import AdminTables from './pages/admin/Tables';
import AdminFloorPlan from './pages/admin/FloorPlan';
import AdminRooms from './pages/admin/Rooms';
import AdminMenu from './pages/admin/Menu';
import AdminUsers from './pages/admin/Users';
import AdminReports from './pages/admin/Reports';
import AdminSettings from './pages/admin/Settings';

import Rental from './pages/admin/rental/Rental';
import HouseListing from './pages/admin/rental/HouseListing';
import Banquet from './pages/admin/banquet/Banquet';
import BanquetPrint from './pages/admin/banquet/BanquetPrint';
import Bazarliq from './pages/admin/purchases/Bazarliq';
import Expenses from './pages/admin/expenses/Expenses';
import Staff from './pages/admin/staff/Staff';
import KassaLedger from './pages/admin/KassaLedger';
import OfisPanel from './pages/admin/OfisPanel';
import Satis from './pages/admin/Satis';
import SayimKassir from './pages/admin/SayimKassir';
import Printers from './pages/admin/Printers';
import Breakfast from './pages/admin/Breakfast';
import PaymentChange from './pages/admin/PaymentChange';
import CancelledOrders from './pages/admin/CancelledOrders';
import ReprintQaime from './pages/admin/ReprintQaime';
import KassaOfisPage from './pages/admin/KassaOfisPage';

import WaiterTables from './pages/waiter/Tables';
import WaiterOrder from './pages/waiter/Order';
import WaiterBill from './pages/waiter/Bill';
import WaiterReceipt from './pages/waiter/Receipt';
import KitchenDisplay from './pages/public/KitchenDisplay';
import QrMenu from './pages/public/QrMenu';
import PayTicket from './pages/public/PayTicket';
import AdminPayment from './pages/admin/Payment';
import FiscalPending from './pages/admin/FiscalPending';
import PublicHouses from './pages/public/Houses';
import PublicBanquet from './pages/public/Banquet';
import BookingResult from './pages/public/BookingResult';

import SuperDashboard from './pages/superadmin/Dashboard';
import SuperTenants from './pages/superadmin/Tenants';
import SuperImpersonate from './pages/superadmin/Impersonate';
import PgImport from './pages/superadmin/PgImport';
import SuperBackup from './pages/superadmin/Backup';
import SuperRestore from './pages/superadmin/Restore';
import SuperNightly from './pages/superadmin/NightlyBackups';
import SuperSettings from './pages/superadmin/Settings';
import SuperLoginBackground from './pages/superadmin/LoginBackground';
import SuperImportV2 from './pages/superadmin/ImportV2Page';

import WarehouseDashboard from './pages/warehouse/Dashboard';

function App() {
    // ── Nativ app token handoff (?token=<JWT>) ──
    // URL-də ?token= varsa → user çəkilənə qədər login səhifəsi GÖRÜNMƏSİN (loader).
    // Token YOXdursa dərhal ready → mövcud axınlar (web/C#/waiter) heç dəyişmir.
    const [ready, setReady] = useState(() => {
        try { return !new URLSearchParams(window.location.search).get('token'); }
        catch { return true; }
    });
    useEffect(() => {
        if (ready) return;
        useAuthStore.getState().bootstrapFromUrlToken().finally(() => setReady(true));
    }, [ready]);

    // ── Token-i həmişə təzə saxla ──
    // Açılışda + hər 3 saatdan bir + proqram yenidən fokuslananda / internet
    // qayıdanda token-i yenilə. Beləcə token iş günü ərzində (8h) bitmir,
    // yeni gün proqram açılanda təzə token gəlir → çap/proqram dayanmır.
    useEffect(() => {
        const refresh = () => {
            if (localStorage.getItem('pos_token')) {
                useAuthStore.getState().refreshToken();
            }
        };
        refresh(); // açılışda (məs. yeni gün proqram açılanda) dərhal təzələ
        const timer = setInterval(refresh, 3 * 60 * 60 * 1000); // hər 3 saat
        const onWake = () => { if (document.visibilityState === 'visible') refresh(); };
        document.addEventListener('visibilitychange', onWake);
        window.addEventListener('focus', refresh);
        window.addEventListener('online', refresh);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onWake);
            window.removeEventListener('focus', refresh);
            window.removeEventListener('online', refresh);
        };
    }, []);

    // ── Desktop app: PC-nin LAN IP-sini DB-yə qeyd et (mobil LAN çap üçün) ──
    // C# WebView-ə window.__fiscalPcLanIp inject edir; yalnız desktopda + login olunubsa işləyir.
    useEffect(() => {
        if (!ready) return;
        const t = setTimeout(() => { import('./utils/printService').then(m => m.registerPcPrintIp()); }, 3000);
        return () => clearTimeout(t);
    }, [ready]);

    // ── Sessiya heartbeat ──
    // Superadmin "çıxış etdir" vuranda (və ya token bitəndə) boş dayanan tab da atılsın:
    // hər 60 san /session-check → 401 gələrsə axios interceptor istifadəçini login-ə yönləndirir.
    // Yalnız görünən tab-da və token varsa ping edilir (yük olmasın).
    useEffect(() => {
        const ping = () => {
            if (document.visibilityState !== 'visible') return;
            if (!localStorage.getItem('pos_token')) return;
            api.get('/session-check').catch(() => { /* 401 → interceptor logout */ });
        };
        const timer = setInterval(ping, 60 * 1000);
        const onWake = () => { if (document.visibilityState === 'visible') ping(); };
        document.addEventListener('visibilitychange', onWake);
        return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onWake); };
    }, []);

    // Token handoff gedərkən qısa loader (login səhifəsi flash etməsin)
    if (!ready) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100dvh', fontFamily: 'DM Sans, sans-serif', color: '#123847', fontSize: 15
            }}>
                Yüklənir…
            </div>
        );
    }

    return (
        <BrowserRouter>
        <QrOrdersProvider>
        <OfflineBanner />
        <QrOrdersModal />
        <ConfirmDialogHost />
        <ShiftClosedPrompt />
        <ImpersonationReturn />
            <Toaster
                position="bottom-center"
                toastOptions={{
                    duration: 2500,
                    style: {
                        background: '#123847',
                        color: '#fff',
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '14px',
                        borderRadius: '99px',
                        padding: '10px 20px',
                    },
                    success: { style: { background: '#1e8449' } },
                    error: { style: { background: '#c0392b' } }
                }}
            />
            <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/kitchen" element={<KitchenDisplay />} />
                <Route path="/qrmenu" element={<QrMenu />} />
                <Route path="/evler" element={<PublicHouses />} />
                <Route path="/rezerv-netice" element={<BookingResult />} />
                <Route path="/banket" element={<PublicBanquet />} />
                <Route path="/odenis" element={<PayTicket />} />
                <Route path="/admin/fiscal-pending" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']}>
                        <FiscalPending />
                    </ProtectedRoute>
                } />
                <Route path="/admin/odenis" element={
                    <ProtectedRoute roles={['admin']}>
                        <AdminPayment />
                    </ProtectedRoute>
                } />

                <Route path="/admin/dashboard" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']}>
                        <AdminDashboard />
                    </ProtectedRoute>
                } />
                <Route path="/admin/tables" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <AdminTables />
                    </ProtectedRoute>
                } />
                <Route path="/admin/floor-plan" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <AdminFloorPlan />
                    </ProtectedRoute>
                } />
                <Route path="/admin/rooms" element={
                    <ProtectedRoute roles={['admin', 'superadmin']} feature="room_management">
                        <AdminRooms />
                    </ProtectedRoute>
                } />
                <Route path="/admin/menu" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'anbardar']}>
                        <AdminMenu />
                    </ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']}>
                        <AdminUsers />
                    </ProtectedRoute>
                } />
                <Route path="/admin/ofisiantlar" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <AdminUsers ofisiantView />
                    </ProtectedRoute>
                } />
                <Route path="/admin/reports" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']} feature="reports">
                        <AdminReports />
                    </ProtectedRoute>
                } />
                <Route path="/admin/settings" element={
                    <ProtectedRoute roles={['admin', 'superadmin']} feature="branding">
                        <AdminSettings />
                    </ProtectedRoute>
                } />

                <Route path="/admin/rental" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']} feature="rental">
                        <Rental />
                    </ProtectedRoute>
                } />
                <Route path="/admin/rental/listing" element={
                    <ProtectedRoute roles={['admin', 'superadmin']} feature="rental">
                        <HouseListing />
                    </ProtectedRoute>
                } />
                <Route path="/admin/banquet/:id/print" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']} feature="menu_sets">
                        <BanquetPrint />
                    </ProtectedRoute>
                } />
                <Route path="/admin/banquet" element={
                    <ProtectedRoute roles={['admin', 'superadmin']} feature="menu_sets">
                        <Banquet />
                    </ProtectedRoute>
                } />
                <Route path="/admin/bazarliq" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']} feature="purchases">
                        <Bazarliq />
                    </ProtectedRoute>
                } />
                <Route path="/admin/expenses" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']} feature="expenses">
                        <Expenses />
                    </ProtectedRoute>
                } />
                <Route path="/admin/staff" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']} feature="staff">
                        <Staff />
                    </ProtectedRoute>
                } />
                <Route path="/admin/kassa-ledger" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']}>
                        <KassaLedger />
                    </ProtectedRoute>
                } />
                <Route path="/admin/ofis" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <OfisPanel />
                    </ProtectedRoute>
                } />
                <Route path="/admin/satis" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']}>
                        <Satis />
                    </ProtectedRoute>
                } />
                <Route path="/admin/sayim" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']} feature="warehouse">
                        <SayimKassir />
                    </ProtectedRoute>
                } />
                <Route path="/admin/printers" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <Printers />
                    </ProtectedRoute>
                } />
                <Route path="/admin/breakfast" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <Breakfast />
                    </ProtectedRoute>
                } />
                <Route path="/admin/payment-change" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <PaymentChange />
                    </ProtectedRoute>
                } />
                <Route path="/admin/cancelled-orders" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <CancelledOrders />
                    </ProtectedRoute>
                } />
                <Route path="/admin/reprint-qaime" element={
                    <ProtectedRoute roles={['admin', 'superadmin']}>
                        <ReprintQaime />
                    </ProtectedRoute>
                } />
                <Route path="/admin/kassa-ofis" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'kassir']}>
                        <KassaOfisPage />
                    </ProtectedRoute>
                } />

                <Route path="/superadmin/dashboard" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperDashboard />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/tenants" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperTenants />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/impersonate" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperImpersonate />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/import" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <PgImport />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/import-v2" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperImportV2 />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/backup" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperBackup />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/restore" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperRestore />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/nightly" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperNightly />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/settings" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperSettings />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin/login-bg" element={
                    <ProtectedRoute roles={['superadmin']}>
                        <SuperLoginBackground />
                    </ProtectedRoute>
                } />

                <Route path="/warehouse/dashboard" element={
                    <ProtectedRoute roles={['admin', 'superadmin', 'anbardar', 'kassir']} feature="warehouse" kassirFlag="kassir_stock_view">
                        <WarehouseDashboard />
                    </ProtectedRoute>
                } />

                <Route path="/waiter/tables" element={
                    <ProtectedRoute roles={['admin', 'waiter', 'superadmin', 'kassir']}>
                        <WaiterTables />
                    </ProtectedRoute>
                } />
                <Route path="/waiter/order/:orderId" element={
                    <ProtectedRoute roles={['admin', 'waiter', 'superadmin', 'kassir']}>
                        <WaiterOrder />
                    </ProtectedRoute>
                } />
                <Route path="/waiter/order/new/:tableId" element={
                    <ProtectedRoute roles={['admin', 'waiter', 'superadmin', 'kassir']}>
                        <WaiterOrder />
                    </ProtectedRoute>
                } />
                <Route path="/waiter/bill/:orderId" element={
                    <ProtectedRoute roles={['admin', 'waiter', 'superadmin', 'kassir']}>
                        <WaiterBill />
                    </ProtectedRoute>
                } />
                <Route path="/waiter/receipt/:orderId" element={
                    <ProtectedRoute roles={['admin', 'waiter', 'superadmin', 'kassir']}>
                        <WaiterReceipt />
                    </ProtectedRoute>
                } />

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </QrOrdersProvider>
        </BrowserRouter>
    );
}

export default App;
